import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar, Clock, ArrowLeft, MessageCircle, Sparkles, Share2 } from 'lucide-react';
import { fetchBlog, fetchBlogs, resolveImageUrl } from '../services/api';
import { Fireflies } from '../components/FairyElements';

const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchBlog(id)
      .then(async (data) => {
        setPost(data);
        try {
          const all = await fetchBlogs();
          setRelatedPosts(all.filter((p) => p.id !== data.id).slice(0, 3));
        } catch {
          setRelatedPosts([]);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id]);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: post.title, text: post.excerpt, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const renderContent = (content) => {
    return content.split('\n\n').map((paragraph, idx) => {
      if (paragraph.startsWith('## ')) {
        return (
          <h2 key={idx} className="text-3xl font-bold mt-10 mb-4 text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>
            {paragraph.replace('## ', '')}
          </h2>
        );
      }
      if (paragraph.startsWith('### ')) {
        return (
          <h3 key={idx} className="text-2xl font-semibold mt-6 mb-3 text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>
            {paragraph.replace('### ', '')}
          </h3>
        );
      }
      if (paragraph.startsWith('**') && paragraph.includes('**')) {
        const parts = paragraph.split('**');
        return (
          <p key={idx} className="mb-4 text-amber-100/85 leading-relaxed">
            {parts.map((part, i) =>
              i % 2 === 1 ? <strong key={i} className="text-amber-300">{part}</strong> : part
            )}
          </p>
        );
      }
      if (paragraph.match(/^\d+\./)) {
        const items = paragraph.split('\n').filter((line) => line.match(/^\d+\./));
        return (
          <ol key={idx} className="list-decimal list-inside mb-4 space-y-2 text-amber-100/85">
            {items.map((item, i) => (
              <li key={i} className="leading-relaxed">{item.replace(/^\d+\.\s*/, '')}</li>
            ))}
          </ol>
        );
      }
      if (paragraph.startsWith('- ')) {
        const items = paragraph.split('\n').filter((line) => line.startsWith('- '));
        return (
          <ul key={idx} className="list-disc list-inside mb-4 space-y-2 text-amber-100/85">
            {items.map((item, i) => (
              <li key={i} className="leading-relaxed">{item.replace('- ', '')}</li>
            ))}
          </ul>
        );
      }
      return (
        <p key={idx} className="mb-4 text-amber-100/85 leading-relaxed">{paragraph}</p>
      );
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a1612] text-amber-200" data-testid="blog-detail-loading">
        Loading article...
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a1612] section-deep" data-testid="blog-not-found">
        <Card className="forest-card border-0 max-w-md w-full mx-4">
          <CardHeader>
            <CardTitle className="text-2xl text-center text-amber-200" style={{ fontFamily: 'Cinzel, serif' }}>
              Article Not Found
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-amber-100/70 mb-6">The article you're looking for doesn't exist.</p>
            <Button asChild className="btn-gold" data-testid="back-to-blog-btn">
              <Link to="/blog">Back to Blog</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1612]">
      <section
        className="forest-bg py-16 px-4 md:px-8 lg:px-16 relative"
        style={{ '--forest-image': "url(/mystical-garden/blog-library.png)", '--bg-opacity': 0.5 }}
      >
        <Fireflies count={30} />

        <div className="max-w-4xl mx-auto relative z-10">
          <Button
            variant="ghost"
            onClick={() => navigate('/blog')}
            className="mb-6 text-amber-300 hover:bg-emerald-900/40 hover:text-amber-200"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blog
          </Button>

          <div className="animate-fadeIn">
            <Badge className="mb-4 bg-emerald-900/60 border border-amber-500/40 text-amber-300 backdrop-blur-md">
              <Sparkles className="w-3 h-3 mr-1.5" />
              {post.category}
            </Badge>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 gradient-text text-glow leading-tight" data-testid="blog-detail-title">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-amber-100/70 mb-6">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2 text-amber-500" />
                {new Date(post.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2 text-amber-500" />
                {post.readTime}
              </div>
              <Button variant="ghost" size="sm" onClick={handleShare} className="text-amber-300 hover:bg-emerald-900/40" data-testid="share-btn">
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 lg:px-16 -mt-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl border-4 border-amber-500/30 mystical-glow-gold">
            <img src={resolveImageUrl(post.image)} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="section-deep py-16 px-4 md:px-8 lg:px-16 relative">
        <Fireflies count={20} />

        <div className="max-w-4xl mx-auto relative z-10">
          <article>
            <p className="text-xl text-amber-200/85 italic mb-10 font-serif-mystical leading-relaxed border-l-4 border-amber-500 pl-6">
              {post.excerpt}
            </p>
            <div data-testid="blog-detail-content">{renderContent(post.content)}</div>
          </article>

          <Card className="forest-card border-0 mt-12">
            <CardContent className="p-10 text-center">
              <h3 className="text-3xl font-bold mb-3 text-amber-300" style={{ fontFamily: 'Cinzel, serif' }}>Ready for Personal Guidance?</h3>
              <p className="text-amber-100/80 mb-6 font-serif-mystical italic">
                Book a personalized reading with The Divine7777 and gain deeper insights into your journey.
              </p>
              <Button size="lg" className="btn-gold px-10 py-6" onClick={() => window.open('https://wa.me/919681424041', '_blank')} data-testid="book-reading-cta">
                <MessageCircle className="mr-2 h-5 w-5" />
                Book a Reading on WhatsApp
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {relatedPosts.length > 0 && (
        <section
          className="forest-bg py-20 px-4 md:px-8 lg:px-16 relative"
          style={{ '--forest-image': "url(/mystical-garden/blog-library.png)", '--bg-opacity': 0.3 }}
        >
          <Fireflies count={20} />

          <div className="max-w-7xl mx-auto relative z-10">
            <h2 className="text-4xl font-bold text-center mb-12 gradient-text">Continue Reading</h2>

            <div className="grid md:grid-cols-3 gap-8">
              {relatedPosts.map((relatedPost) => (
                <Card
                  key={relatedPost.id}
                  className="forest-card border-0 overflow-hidden group cursor-pointer"
                  onClick={() => navigate(`/blog/${relatedPost.id}`)}
                  data-testid={`related-post-${relatedPost.id}`}
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={resolveImageUrl(relatedPost.image)}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 opacity-90"
                    />
                  </div>
                  <CardHeader>
                    <Badge className="mb-2 w-fit bg-emerald-800/80 text-amber-300 border border-amber-500/30">{relatedPost.category}</Badge>
                    <CardTitle className="text-lg text-amber-200 group-hover:text-amber-300 transition-colors line-clamp-2" style={{ fontFamily: 'Cinzel, serif' }}>
                      {relatedPost.title}
                    </CardTitle>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetail;
